/*
 * Full Hero + Text video controls — scoped per [data-full-hero-root] root.
 * Loaded only when at least one video setting is filled.
 * Hooks: [data-full-hero-root], [data-media-card], [data-video-toggle], [data-audio-toggle].
 */

class FullHeroVideos {
  constructor(root) {
    this.root = root;
    this.buttons = root.querySelectorAll('[data-video-toggle]');
    this.audioButtons = root.querySelectorAll('[data-audio-toggle]');
    this.videos = root.querySelectorAll('video');

    if (!this.videos.length) return;

    this.initBreakpointGate();
    this.initVideoControls();
    this.initAudioControls();
    this.initViewportPause();
  }

  // Each video is tagged with `data-breakpoint` (mobile/desktop/all) by Liquid.
  // When a section has both mobile and desktop videos rendered side-by-side,
  // only the active breakpoint's video plays — the other is paused with
  // `data-bp-paused` so it doesn't burn bandwidth.
  initBreakpointGate() {
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');

    this.videos.forEach((video) => {
      if (!video.dataset.breakpoint) video.dataset.breakpoint = 'all';
    });

    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.desktopQuery.addEventListener?.('change', this.handleBreakpointChange);
    this.applyBreakpointGate();
  }

  videoMatchesBreakpoint(video) {
    const bp = video.dataset.breakpoint;
    const isDesktop = this.desktopQuery?.matches ?? true;
    return bp === 'all' || (bp === 'desktop' && isDesktop) || (bp === 'mobile' && !isDesktop);
  }

  applyBreakpointGate() {
    this.videos.forEach((video) => {
      if (this.videoMatchesBreakpoint(video)) {
        if (video.dataset.bpPaused === 'true') {
          delete video.dataset.bpPaused;
          if (video.dataset.userPaused !== 'true' && video.dataset.inViewport !== 'false') {
            video.play().catch(() => {});
          }
        }
      } else {
        video.dataset.bpPaused = 'true';
        if (!video.paused) video.pause();
      }
    });
    this.updateButtons();
  }

  handleBreakpointChange() {
    this.applyBreakpointGate();
  }

  getActiveVideo(mediaCard) {
    if (!mediaCard) return null;
    const videos = mediaCard.querySelectorAll('video');
    if (!videos.length) return null;
    return Array.from(videos).find((video) => this.videoMatchesBreakpoint(video)) || videos[0];
  }

  initVideoControls() {
    this.buttons.forEach((btn) => {
      const mediaCard = btn.closest('[data-media-card]');
      const cardVideos = mediaCard ? mediaCard.querySelectorAll('video') : [];
      if (!cardVideos.length) return;

      btn.setAttribute('data-video-ready', 'true');
      cardVideos.forEach((video) => {
        video.controls = false;
      });

      cardVideos.forEach((video) => {
        video.addEventListener('play', () => {
          if (video.dataset.bpPaused === 'true') {
            video.pause();
            return;
          }
          if (video.dataset.inViewport === 'false' && video.dataset.userPaused !== 'true') {
            video.dataset.autoPaused = 'true';
            video.pause();
            return;
          }
          const activeVideo = this.getActiveVideo(mediaCard);
          if (video === activeVideo) this.setVideoState(btn, video);
        });

        video.addEventListener('pause', () => {
          const activeVideo = this.getActiveVideo(mediaCard);
          if (video === activeVideo) this.setVideoState(btn, video);
        });
      });

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const video = this.getActiveVideo(mediaCard);
        if (!video) return;
        if (video.paused) {
          delete video.dataset.userPaused;
          delete video.dataset.autoPaused;
          video.play().catch(() => {});
        } else {
          video.dataset.userPaused = 'true';
          delete video.dataset.autoPaused;
          video.pause();
        }
        this.setVideoState(btn, video);
      });

      const activeVideo = this.getActiveVideo(mediaCard);
      if (activeVideo) {
        // Seed optimistic: assume the video will autoplay, so the discrete-mode
        // button stays hidden. Only flip to paused if autoplay is genuinely blocked.
        btn.dataset.videoState = 'playing';
        btn.setAttribute('aria-label', 'Pause video');
        btn.setAttribute('aria-pressed', 'true');
        this.mirrorAudioButtonState(mediaCard, 'playing');

        const playProbe = activeVideo.play();
        if (playProbe && typeof playProbe.then === 'function') {
          // An AbortError is our own pause() interrupting the probe; only a refusal means paused.
          playProbe.catch((err) => {
            if (err && err.name === 'AbortError') return;
            this.setVideoState(btn, activeVideo, { force: true });
          });
        }
      }
    });
  }

  // The audio button shares the play/pause button's discrete-mode reveal
  // trigger (`data-video-state`) so both controls appear/hide together —
  // hidden during smooth autoplay, revealed whenever the video is paused.
  mirrorAudioButtonState(mediaCard, videoState) {
    const audioBtn = mediaCard?.querySelector('[data-audio-toggle]');
    if (audioBtn) audioBtn.dataset.videoState = videoState;
  }

  initAudioControls() {
    this.audioButtons.forEach((btn) => {
      const mediaCard = btn.closest('[data-media-card]');
      const cardVideos = mediaCard ? mediaCard.querySelectorAll('video') : [];
      if (!cardVideos.length) return;

      btn.setAttribute('data-video-ready', 'true');
      this.setAudioState(btn, (this.getActiveVideo(mediaCard) || cardVideos[0]).muted);

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const activeVideo = this.getActiveVideo(mediaCard) || cardVideos[0];
        const nextMuted = !activeVideo.muted;
        cardVideos.forEach((video) => {
          video.muted = nextMuted;
        });
        this.setAudioState(btn, nextMuted);
      });
    });
  }

  setAudioState(btn, isMuted) {
    if (!btn) return;
    btn.setAttribute('aria-label', isMuted ? 'Unmute audio' : 'Mute audio');
    btn.setAttribute('aria-pressed', String(!isMuted));
    btn.dataset.audioState = isMuted ? 'muted' : 'unmuted';
  }

  initViewportPause() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.videos.forEach((video) => {
        video.dataset.userPaused = 'true';
        video.pause();
      });
      this.updateButtons();
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    // Observe the section root so a single observer handles every video.
    // Videos pause when the whole section leaves the viewport vertically.
    this.viewportObserver = new IntersectionObserver(
      ([entry]) => {
        const inViewport = entry.isIntersecting;

        this.videos.forEach((video) => {
          video.dataset.inViewport = inViewport ? 'true' : 'false';

          if (inViewport) {
            if (
              video.dataset.autoPaused === 'true' &&
              video.dataset.userPaused !== 'true' &&
              video.dataset.bpPaused !== 'true'
            ) {
              delete video.dataset.autoPaused;
              video.play().catch(() => {
                video.dataset.autoPaused = 'true';
              });
            }
            return;
          }

          if (!video.paused && video.dataset.userPaused !== 'true') {
            video.dataset.autoPaused = 'true';
            video.pause();
          }
        });

        this.updateButtons();
      },
      { threshold: 0.1 },
    );

    this.viewportObserver.observe(this.root);
  }

  updateButtons() {
    this.buttons.forEach((btn) => {
      const mediaCard = btn.closest('[data-media-card]');
      const video = this.getActiveVideo(mediaCard);
      if (video) this.setVideoState(btn, video);
    });
  }

  setVideoState(btn, video, options) {
    if (!btn || !video) return;
    // A pause before playback has begun is start-up noise from our own viewport/breakpoint handling,
    // not a user action. `force` marks the cases that must still show the button.
    if (!(options && options.force) && video.paused && video.played.length === 0) return;
    const isPaused = video.paused;
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    btn.setAttribute('aria-pressed', String(!isPaused));
    btn.dataset.videoState = isPaused ? 'paused' : 'playing';
    this.mirrorAudioButtonState(btn.closest('[data-media-card]'), btn.dataset.videoState);
  }
}

function initSections(container = document) {
  const selector = '[data-full-hero-root]:not([data-full-hero-videos-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => {
    root.dataset.fullHeroVideosInit = 'true';
    new FullHeroVideos(root);
  });
}

initSections();
document.addEventListener('shopify:section:load', (event) => initSections(event.target));
